import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Mic, Music } from "lucide-react";
import SEO from "@/components/SEO";
import EditorialMarquee from "@/components/Marquee";
import AudioPlayer from "@/components/AudioPlayer";
import CTASection from "@/components/CTASection";
import { MaskedLines, FadeUp, SectionTag, EASE } from "@/components/Reveal";
import { IMAGES, MANIFESTO, TESTIMONIALS, CEREMONIES, SERVICES, BUSINESS_LD, WEBSITE_LD, SERVICE_LD, mailto } from "@/data/content";

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div data-testid="home-page">
      <SEO
        title="Trauredner Krefeld & NRW | Freie Trauung – Daviz & Puxi"
        description="Freie Trauung in Krefeld, NRW und deutschlandweit. Persönliche Traureden von Mark Daviz & Angela Puxi – auf Wunsch mit Live-Gesang oder Saxophon. Jetzt unverbindlich kennenlernen."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [BUSINESS_LD(origin), WEBSITE_LD(origin), SERVICE_LD(origin)],
        }}
      />

      {/* HERO */}
      <section ref={heroRef} data-testid="home-hero" className="relative flex min-h-[100svh] items-end overflow-hidden">
        <motion.div className="absolute inset-0" style={{ y: bgY }}>
          <img
            src={IMAGES.hero}
            alt="Rosé-Rosen und Brautpaar bei einer freien Trauung im warmen Abendlicht"
            fetchPriority="high"
            className="h-[115%] w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/35 to-ink/20" />
          <div className="absolute inset-0 bg-moss/20 mix-blend-multiply" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-40 sm:px-8 sm:pb-24">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8, ease: EASE }}
            className="mb-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-sand"
          >
            <span className="h-px w-10 bg-sand" /> Freie Redner · Mark Daviz & Angela Puxi
          </motion.p>
          <MaskedLines
            lines={["Freie Trauung", "in Krefeld & NRW"]}
            className="font-serif text-5xl leading-[1.04] text-creme sm:text-7xl lg:text-8xl"
            delay={0.35}
          />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.9, ease: EASE }}
            className="mt-6 font-accent text-2xl italic text-sand sm:text-3xl"
          >
            Worte, die bleiben. Momente, die tragen.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.9, ease: EASE }}
            className="mt-6 max-w-xl text-base leading-relaxed text-creme/85 sm:text-lg"
          >
            Wir gestalten individuelle Zeremonien für die besonderen Momente Ihres Lebens –
            freie Trauungen, Willkommensfeiern und unvergessliche Jubiläen. Persönlich geschrieben,
            von Menschen gehalten, die zuhören.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
            className="mt-9 flex flex-col gap-4 sm:flex-row"
          >
            <a
              href={mailto("Anfrage Zeremonie")}
              data-testid="hero-cta-primary"
              className="inline-flex items-center justify-center gap-3 rounded-full bg-creme px-9 py-4 text-sm font-bold text-ink transition-transform duration-300 hover:scale-[1.04]"
            >
              Zeremonie anfragen <ArrowRight size={16} />
            </a>
            <Link
              to="/leistungen"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-3 rounded-full border border-creme/40 px-9 py-4 text-sm font-semibold text-creme backdrop-blur-sm transition-colors duration-300 hover:border-sand hover:text-sand"
            >
              Mehr über uns erfahren
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 sm:block"
        >
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2 }}>
            <ArrowDown size={18} className="text-creme/60" />
          </motion.div>
        </motion.div>
      </section>

      <EditorialMarquee
        items={["Freie Trauungen", "Willkommensfeiern", "Eheversprechen", "Jubiläen", "Lebensfeiern", "Individuelle Reden"]}
      />

      {/* INTRO */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-36">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <FadeUp>
              <SectionTag>Schön, dass Sie da sind</SectionTag>
              <h2 className="mt-6 font-serif text-4xl leading-[1.1] sm:text-5xl">
                Keine Zeremonie<br />von der Stange.<br />
                <span className="font-accent italic text-rust">Versprochen.</span>
              </h2>
            </FadeUp>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <FadeUp delay={0.15}>
              <p className="text-lg leading-relaxed text-stone">
                Es gibt Momente im Leben, die zu wichtig sind für Floskeln. Der erste Blick vor dem
                Ja-Wort. Der Name, der zum ersten Mal laut ausgesprochen wird. Das Fest, auf das
                alle Jahre gewartet haben.
              </p>
              <p className="mt-6 text-lg leading-relaxed text-stone">
                Wir – Mark Daviz und Angela Puxi – schreiben und sprechen für diese Momente.
                Nicht aus Vorlagen, sondern aus Ihren Erzählungen. Jede Rede entsteht einmal,
                für genau einen Anlass, und wird nie wieder gehalten.
              </p>
              <div className="mt-8 flex flex-wrap gap-6">
                <Link
                  to="/zeremonien"
                  data-testid="intro-link-zeremonien"
                  className="link-underline inline-flex items-center gap-2 text-sm font-semibold text-moss"
                >
                  Unsere Zeremonien entdecken <ArrowRight size={15} />
                </Link>
                <Link
                  to="/freie-trauung-krefeld"
                  data-testid="intro-link-krefeld"
                  className="link-underline inline-flex items-center gap-2 text-sm font-semibold text-rust"
                >
                  Freie Trauung in Krefeld <ArrowRight size={15} />
                </Link>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* PEOPLE */}
      <section data-testid="home-people" className="bg-parch py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeUp className="mb-14 flex flex-wrap items-end justify-between gap-6">
            <div>
              <SectionTag>Zwei Menschen, ein Anspruch</SectionTag>
              <h2 className="mt-5 font-serif text-4xl sm:text-5xl">Die Stimmen hinter<br />den Worten</h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-stone">
              Der wichtigste Unterschied zu anderen? Wir. Lernen Sie uns kennen – bevor Sie uns Ihren Moment anvertrauen.
            </p>
          </FadeUp>

          <div className="grid gap-8 md:grid-cols-2">
            {[
              {
                name: "Mark Daviz",
                img: IMAGES.mark,
                role: "Freier Trauredner & Sänger",
                line: "„Die schönsten Reden entstehen nicht am Schreibtisch. Sie entstehen beim Zuhören.“",
                to: "/mark-daviz",
                id: "mark",
              },
              {
                name: "Angela Puxi",
                img: IMAGES.angela,
                role: "Freie Rednerin & Saxophonistin",
                line: "„Eine gute Rede entsteht, wenn man einen Menschen wirklich ernst nimmt.“",
                to: "/angela-puxi",
                id: "angela",
              },
            ].map((p, i) => (
              <FadeUp key={p.id} delay={i * 0.15}>
                <Link to={p.to} data-testid={`home-person-${p.id}`} className="group block">
                  <div className="relative overflow-hidden border border-sand">
                    <img
                      src={p.img}
                      alt={`${p.name} – ${p.role}, freie Trauung Krefeld & NRW`}
                      style={{ objectPosition: p.id === "angela" ? "center 22%" : "center 12%" }}
                      loading="lazy"
                      className="aspect-[4/5] w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.05]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/55 via-transparent to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-7">
                      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sand">{p.role}</p>
                      <p className="mt-2 font-serif text-3xl text-creme">{p.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-4 border-x border-b border-sand bg-creme px-7 py-5">
                    <p className="font-accent text-lg italic leading-snug text-stone">{p.line}</p>
                    <ArrowRight size={18} className="shrink-0 text-rust transition-transform duration-300 group-hover:translate-x-1.5" />
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES TEASER (numbered editorial) */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
        <FadeUp className="mb-14">
          <SectionTag>Was wir für Sie tun</SectionTag>
          <h2 className="mt-5 max-w-2xl font-serif text-4xl sm:text-5xl">
            Von der ersten Tasse Kaffee<br />bis zum letzten <span className="font-accent italic text-rust">Händedruck.</span>
          </h2>
        </FadeUp>
        <div className="grid gap-x-12 gap-y-2 md:grid-cols-2">
          {SERVICES.slice(0, 6).map((s, i) => (
            <FadeUp key={s.title} delay={(i % 2) * 0.1}>
              <div className="group flex gap-6 border-b border-sand/70 py-7">
                <span className="font-accent text-3xl italic text-sand">0{i + 1}</span>
                <div>
                  <h3 className="font-serif text-xl transition-colors duration-300 group-hover:text-rust">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stone">{s.text}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
        <FadeUp className="mt-10">
          <Link
            to="/leistungen"
            data-testid="home-services-link"
            className="inline-flex items-center gap-3 rounded-full border border-ink/20 px-8 py-3.5 text-sm font-semibold text-ink transition-colors duration-300 hover:border-moss hover:bg-moss hover:text-creme"
          >
            Alle Leistungen im Überblick <ArrowRight size={15} />
          </Link>
        </FadeUp>
      </section>

      {/* CEREMONIES TETRIS GRID */}
      <section data-testid="home-ceremonies" className="bg-mossdeep py-24 text-creme sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeUp className="mb-14 flex flex-wrap items-end justify-between gap-6">
            <div>
              <SectionTag dark>Zeremonien</SectionTag>
              <h2 className="mt-5 font-serif text-4xl sm:text-5xl">
                Für jeden Anlass<br />die <span className="font-accent italic text-sand">richtigen Worte</span>
              </h2>
            </div>
            <Link to="/zeremonien" data-testid="home-ceremonies-link" className="link-underline inline-flex items-center gap-2 text-sm font-semibold text-sand">
              Alle Zeremonien ansehen <ArrowRight size={15} />
            </Link>
          </FadeUp>

          <div className="grid auto-rows-[190px] grid-cols-2 gap-4 sm:auto-rows-[220px] lg:grid-cols-4">
            {[
              { c: CEREMONIES[0], cls: "col-span-2 row-span-2" },
              { c: CEREMONIES[2], cls: "col-span-2 row-span-1 lg:col-span-1" },
              { c: CEREMONIES[5], cls: "col-span-2 row-span-1 lg:col-span-1" },
              { c: CEREMONIES[4], cls: "col-span-1 row-span-1" },
              { c: CEREMONIES[6], cls: "col-span-1 row-span-1" },
            ].map(({ c, cls }, i) => (
              <FadeUp key={c.slug} delay={i * 0.08} className={cls}>
                <Link
                  to={`/zeremonien#${c.slug}`}
                  data-testid={`home-ceremony-${c.slug}`}
                  className="group relative block h-full w-full overflow-hidden"
                >
                  <img
                    src={c.image}
                    alt={`${c.title} – Zeremonie von Daviz & Puxi`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/75 via-ink/10 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                    <h3 className="font-serif text-xl text-creme sm:text-2xl">{c.title}</h3>
                    <p className="mt-1 hidden text-xs leading-relaxed text-creme/70 sm:block sm:max-w-xs">{c.teaser}</p>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* LIVE MUSIK USP */}
      <section data-testid="home-live-music" className="relative overflow-hidden py-24 sm:py-32">
        <img src={IMAGES.microphone} alt="Live-Gesang und Saxophon zur Zeremonie" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-mossdeep/90" />
        <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
          <FadeUp className="max-w-3xl">
            <SectionTag dark>Unser besonderes Extra</SectionTag>
            <h2 className="mt-6 font-serif text-4xl leading-[1.08] text-creme sm:text-6xl">
              Ihr Lied. Live.<br />
              <span className="font-accent italic text-sand">Nur für Sie.</span>
            </h2>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-creme/80">
              Ihr Lied – live performt: Mark Daviz singt Ihren Song live zur Zeremonie,
              Angela Puxi spielt Ihr Lied live mit Saxophon zur Zeremonie. Auch kann der
              jeweils andere dazugebucht werden und komplettiert Ihre Feier zu einem
              unvergesslichen Moment. Kein Playback, keine Konserve – Ihre Geschichte
              und Ihr Lied, live.
            </p>
          </FadeUp>
          <div className="mt-12 grid gap-6 sm:grid-cols-2">
            <FadeUp delay={0.1}>
              <div className="border border-creme/20 p-8">
                <Mic size={22} className="text-sand" />
                <p className="mt-4 text-xs font-semibold uppercase tracking-[0.25em] text-sand">Mark Daviz</p>
                <p className="mt-2 font-serif text-2xl text-creme">Gesang</p>
                <p className="mt-3 text-sm leading-relaxed text-creme/70">
                  Ihre Geschichte verdient mehr als Worte – Mark singt Ihr Lied mit derselben
                  Stimme, die Sie aus unseren Hörbeispielen kennen.
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <div className="border border-creme/20 p-8">
                <Music size={22} className="text-sand" />
                <p className="mt-4 text-xs font-semibold uppercase tracking-[0.25em] text-sand">Angela Puxi</p>
                <p className="mt-2 font-serif text-2xl text-creme">Saxophon</p>
                <p className="mt-3 text-sm leading-relaxed text-creme/70">
                  Angela begleitet am Saxophon – warm, nah und genau im richtigen Moment.
                </p>
              </div>
            </FadeUp>
          </div>
          <FadeUp delay={0.3}>
            <Link
              to="/hoerbeispiele"
              data-testid="live-music-audio-link"
              className="mt-10 inline-flex items-center gap-3 rounded-full bg-creme px-8 py-4 text-sm font-bold text-mossdeep transition-transform duration-300 hover:scale-[1.03]"
            >
              Hörbeispiele anhören <ArrowRight size={15} />
            </Link>
          </FadeUp>
        </div>
      </section>

      {/* MANIFESTO */}
      <section data-testid="home-manifesto" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-36">
        <FadeUp className="mb-16 max-w-2xl">
          <SectionTag>Unsere Philosophie</SectionTag>
          <h2 className="mt-5 font-serif text-4xl sm:text-5xl">
            Vier Überzeugungen,<br />ein <span className="font-accent italic text-rust">Versprechen</span>
          </h2>
        </FadeUp>
        <div className="grid gap-x-16 gap-y-14 sm:grid-cols-2">
          {MANIFESTO.map((m, i) => (
            <FadeUp key={m.no} delay={(i % 2) * 0.12}>
              <div className="flex gap-7">
                <span className="font-accent text-6xl italic leading-none text-sand sm:text-7xl">{m.no}</span>
                <div className="pt-2">
                  <h3 className="font-serif text-2xl">{m.title}</h3>
                  <p className="mt-3 max-w-sm leading-relaxed text-stone">{m.text}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-parch py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeUp className="mb-14">
            <SectionTag>Kundenstimmen</SectionTag>
            <h2 className="mt-5 font-serif text-4xl sm:text-5xl">Was Menschen<br />uns zurückmelden</h2>
          </FadeUp>
          <div className="grid gap-10 md:grid-cols-3">
            {TESTIMONIALS.map((t, i) => (
              <FadeUp key={i} delay={i * 0.12}>
                <blockquote data-testid={`testimonial-${i}`} className="flex h-full flex-col border-l-2 border-rust/60 pl-6">
                  <p className="font-accent text-2xl italic leading-snug text-ink">„{t.quote}“</p>
                  <footer className="mt-6 text-sm">
                    <p className="font-semibold text-ink">{t.name}</p>
                    <p className="text-stone">{t.context}</p>
                  </footer>
                </blockquote>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIO TEASER */}
      <section data-testid="home-audio-teaser" className="bg-mossdeep py-24 text-creme sm:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <FadeUp>
            <SectionTag dark>Hörbeispiele</SectionTag>
            <h2 className="mt-5 font-serif text-4xl leading-[1.1] sm:text-5xl">
              Eine Stimme sagt mehr als<br />
              <span className="font-accent italic text-sand">tausend geschriebene Worte.</span>
            </h2>
            <p className="mt-6 max-w-md leading-relaxed text-creme/70">
              Bevor Sie uns anrufen: Hören Sie uns zu. Unsere Hörbeispiele geben Ihnen einen
              echten Eindruck davon, wie unsere Reden klingen – ruhig, persönlich, nah.
            </p>
            <Link
              to="/hoerbeispiele"
              data-testid="home-audio-link"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-creme px-8 py-4 text-sm font-bold text-mossdeep transition-transform duration-300 hover:scale-[1.03]"
            >
              Hörbeispiele anhören <ArrowRight size={15} />
            </Link>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:pl-10">
            <AudioPlayer index={100} dark title="Persönliche Vorstellung" speaker="Mark Daviz" duration="1:24" description="Wer ich bin, warum ich Redner wurde – und was Sie erwarten dürfen." />
            <AudioPlayer index={101} dark title="Persönliche Vorstellung" speaker="Angela Puxi" duration="1:38" description="Ein kurzer Einblick in meine Stimme, meine Haltung, meine Arbeit." />
          </FadeUp>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default Home;
